import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, MessageCircle, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtQty, fmtDT } from "@/lib/api";

async function slipPdf(name) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const canvas = await html2canvas(document.querySelector(".print-area"), {
    scale: 2, backgroundColor: "#ffffff",
    onclone: (d) => { const s = d.createElement("style"); s.textContent = ".print-area td,.print-area th{padding-bottom:8px!important;vertical-align:top}"; d.head.appendChild(s); },
  });
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const w = 190, h = Math.min((canvas.height * w) / canvas.width, 277);
  pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 10, 10, w, h);
  return new File([pdf.output("blob")], name, { type: "application/pdf" });
}

const TITLES = { quote: "QUOTATION", slip: "CABLE CUTTING SLIP", bill: "CUTTING SLIP · BILL SUMMARY", challan: "DELIVERY CHALLAN", gatepass: "GATE PASS", handover: "MATERIAL HANDOVER SLIP" };
const cell = "border border-black px-2 py-1";

function waText(b) {
  const lines = b.items.map((c, i) => `${i + 1}. ${c.brand} ${c.cable_size} ${c.product_name.replace(`${c.brand} `, "").replace(`${c.cable_size} `, "")} – *${fmtQty(c.cut_length, c.uom)}*`);
  const total = Object.entries(b.totals).map(([u, q]) => fmtQty(q, u)).join(" + ");
  return [`*ROXY CABLES – Cutting Slip*`, `Bill No: *${b.bill_no}*`, `Party: ${b.party_name || "—"}`, `Date: ${fmtDT(b.items[0].at)}`, "", ...lines, "", `*Total cut: ${total}*`,
    b.taken_by ? `Taken by: ${b.taken_by}` : "", b.vehicle_no ? `Vehicle: ${b.vehicle_no}` : "", "", "Thank you – Roxy Cables, Bangalore"].filter((x, i, a) => x !== "" || a[i - 1] !== "").join("\n");
}

function dispText(d, type) {
  const no = type === "gatepass" ? d.gate_pass_no : d.dispatch_no;
  const lines = d.items.map((it, i) => `${i + 1}. ${it.brand} ${it.cable_size} – ${it.unit_code} – *${fmtQty(it.qty, it.uom)}*`);
  return [`*ROXY CABLES – ${TITLES[type]}*`, `${type === "gatepass" ? "Gate Pass" : "Dispatch"} No: *${no}*`, `Bill No: ${d.bill_no}`, `Party: ${d.party_name || "—"}`, `Date: ${d.date}`,
    d.vehicle_no ? `Vehicle: ${d.vehicle_no}` : "", "", ...lines, "", "Thank you – Roxy Cables, Bangalore"].filter((x, i, a) => x !== "" || a[i - 1] !== "").join("\n");
}

function quoteText(q) {
  const lines = q.items.map((it, i) => `${i + 1}. ${it.description} – ${fmtQty(it.qty, it.uom)} @ ₹${it.rate} = ₹${it.amount.toLocaleString("en-IN")}`);
  return [`*ROXY CABLES – Quotation ${q.quote_no}*`, `Party: ${q.party_name}`, `Date: ${q.date}`, "", ...lines, "", `Subtotal: ₹${q.subtotal.toLocaleString("en-IN")}`, `GST ${q.gst_pct}%: ₹${q.gst.toLocaleString("en-IN")}`, `*Total: ₹${q.total.toLocaleString("en-IN")}*`, `Valid for ${q.validity_days} days`, "", "Thank you – Roxy Cables, Bangalore"].join("\n");
}

function QuoteBody({ q }) {
  const inr = (v) => `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (<>
    <div className="grid grid-cols-2 gap-x-6 gap-y-1"><Row k="Quotation No" v={q.quote_no} /><Row k="Date" v={q.date} /><Row k="To" v={q.party_name} /><Row k="Mobile" v={q.mobile} /><Row k="Valid for" v={`${q.validity_days} days`} /><Row k="Prepared by" v={q.created_by?.name} /></div>
    <table className="w-full mt-4 text-[13px] border border-black border-collapse" data-testid="quote-print-table">
      <thead><tr className="bg-gray-100">{["#", "Description", "Qty", "Rate", "Amount"].map((h) => <th key={h} className={`${cell} text-left`}>{h}</th>)}</tr></thead>
      <tbody>{q.items.map((it, i) => <tr key={i}><td className={cell}>{i + 1}</td><td className={cell}>{it.description}</td><td className={`${cell} font-mono text-right`}>{fmtQty(it.qty, it.uom)}</td><td className={`${cell} font-mono text-right`}>{inr(it.rate)}</td><td className={`${cell} font-mono text-right`}>{inr(it.amount)}</td></tr>)}</tbody>
      <tfoot>
        <tr><td className={cell} colSpan={4}>Subtotal</td><td className={`${cell} font-mono text-right`}>{inr(q.subtotal)}</td></tr>
        <tr><td className={cell} colSpan={4}>GST @ {q.gst_pct}%</td><td className={`${cell} font-mono text-right`}>{inr(q.gst)}</td></tr>
        <tr><td className={`${cell} font-bold`} colSpan={4}>TOTAL</td><td className={`${cell} font-mono text-right font-bold`} data-testid="quote-print-total">{inr(q.total)}</td></tr>
      </tfoot>
    </table>
    {q.notes && <p className="text-[13px] mt-3"><b>Notes:</b> {q.notes}</p>}
    <p className="text-[11px] text-gray-600 mt-2">Prices subject to stock availability at the time of order.</p>
    <div className="flex gap-8 mt-12"><div className="flex-1" /><Sign label="For ROXY CABLES" /></div>
  </>);
}

function waProps(doc, type) {
  if (type === "quote") return { mobile: doc.mobile || "", name: `Roxy_Quotation_${doc.quote_no}`, title: `Quotation ${doc.quote_no}`, text: quoteText(doc) };
  if (type === "bill") return { mobile: (doc.items.find((c) => c.mobile) || {}).mobile || "", name: `Roxy_Cutting_Slip_${doc.bill_no}`, title: `Cutting Slip ${doc.bill_no}`, text: waText(doc) };
  const no = type === "gatepass" ? doc.gate_pass_no : doc.dispatch_no;
  return { mobile: type === "gatepass" ? doc.driver_mobile || "" : doc.handover?.recipient_mobile || doc.driver_mobile || "",
    name: `Roxy_${type === "gatepass" ? "Gate_Pass" : "Delivery_Challan"}_${no}`, title: `${TITLES[type]} ${no}`, text: dispText(doc, type) };
}

function DownloadPdf({ name, auto }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const file = await slipPdf(`${name.replace(/[^\w-]/g, "_")}.pdf`);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(file); a.download = file.name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch { toast.error("Could not create the PDF"); }
    setBusy(false);
  };
  useEffect(() => { if (auto) { const t = setTimeout(go, 600); return () => clearTimeout(t); } return undefined; }, [auto]); // eslint-disable-line react-hooks/exhaustive-deps
  return <Button variant="outline" onClick={go} disabled={busy} data-testid="download-pdf-btn"><Download className="h-4 w-4 mr-1" />{busy ? "Making PDF…" : "Download PDF"}</Button>;
}

function WhatsAppSend({ w }) {
  const [mob, setMob] = useState(w.mobile);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const file = await slipPdf(`${w.name.replace(/[^\w-]/g, "_")}.pdf`);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: w.title, text: w.text });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(file); a.download = file.name; a.click();
        let n = mob.replace(/\D/g, "");
        if (n.length === 10) n = `91${n}`;
        window.open(`https://wa.me/${n}?text=${encodeURIComponent(w.text)}`, "_blank");
        toast.info("PDF downloaded – attach it in the WhatsApp chat that opened");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not create the PDF");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <Input value={mob} onChange={(e) => setMob(e.target.value)} placeholder="WhatsApp number" inputMode="tel" className="w-48 bg-white" data-testid="bill-whatsapp-number" />
      <Button className="bg-[#25D366] hover:bg-[#1ebe5a] text-white" onClick={send} disabled={busy} data-testid="bill-whatsapp-btn"><MessageCircle className="h-4 w-4 mr-1" />{busy ? "Making PDF…" : "Send PDF on WhatsApp"}</Button>
    </div>
  );
}

function BillBody({ b }) {
  return (<>
    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
      <Row k="Bill No" v={b.bill_no} /><Row k="Party" v={b.party_name} /><Row k="Taken by" v={b.taken_by} /><Row k="Vehicle" v={b.vehicle_no} />
      <Row k="Mobile" v={b.mobile} /><Row k="Cables" v={`${b.items.length}`} />
    </div>
    <table className="w-full mt-4 text-[13px] border border-black border-collapse" data-testid="bill-slip-table">
      <thead><tr className="bg-gray-100">{["#", "Slip No", "Date / Time", "Brand", "Cable", "Drum", "Cut Length", "Drum Balance"].map((h) => <th key={h} className={`${cell} text-left`}>{h}</th>)}</tr></thead>
      <tbody>{b.items.map((c, i) => (
        <tr key={c.id}><td className={cell}>{i + 1}</td><td className={`${cell} font-mono`}>{c.slip_no}</td><td className={cell}>{fmtDT(c.at)}</td><td className={cell}>{c.brand}</td>
          <td className={cell}>{c.cable_size} · {c.product_name}</td><td className={`${cell} font-mono`}>{c.mfr_drum_no || c.drum_code}</td>
          <td className={`${cell} font-mono text-right font-bold`}>{fmtQty(c.cut_length, c.uom)}</td><td className={`${cell} font-mono text-right`}>{fmtQty(c.drum_balance_after, c.uom)}</td></tr>
      ))}</tbody>
      <tfoot><tr><td className={`${cell} font-bold`} colSpan={6}>TOTAL CUT</td><td className={`${cell} font-mono text-right font-bold`} colSpan={2} data-testid="bill-slip-total">{Object.entries(b.totals).map(([u, q]) => fmtQty(q, u)).join(" + ")}</td></tr></tfoot>
    </table>
    <div className="flex gap-8 mt-12"><Sign label="Cut by" /><Sign label="Received by (signature)" /><Sign label="Authorised signatory" /></div>
  </>);
}

function SigImage({ id }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let u;
    api.get(`/attachments/${id}/download`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); });
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [id]);
  return url ? <img src={url} alt="Recipient signature" className="h-16 object-contain" data-testid="print-signature-img" /> : null;
}

const Row = ({ k, v }) => <div className="flex gap-2 text-[13px]"><span className="w-40 shrink-0 text-gray-500">{k}</span><span className="font-medium">{v || "—"}</span></div>;
const Sign = ({ label, children }) => <div className="flex-1 text-center text-xs"><div className="h-16 flex items-end justify-center">{children}</div><div className="border-t border-black pt-1">{label}</div></div>;

function SlipBody({ c }) {
  return (<>
    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
      <Row k="Slip No" v={c.slip_no} /><Row k="Entry ID" v={c.cutting_no} /><Row k="Date / Time" v={fmtDT(c.at)} /><Row k="Bill No" v={c.bill_no} />
      <Row k="Party" v={c.party_name} /><Row k="Brand" v={c.brand} /><Row k="Cable" v={`${c.cable_size} · ${c.product_name}`} /><Row k="Drum" v={`${c.drum_code} / Mfr ${c.mfr_drum_no || "—"}`} />
      <Row k="Drum balance before" v={fmtQty(c.drum_balance_before, c.uom)} /><Row k="Remaining on drum" v={fmtQty(c.drum_balance_after, c.uom)} />
      <Row k="Cut piece ID" v={c.cut_piece_code} /><Row k="Taken by" v={c.taken_by} /><Row k="Vehicle" v={c.vehicle_no} /><Row k="Mobile" v={c.mobile} /><Row k="Cut by" v={c.cut_by} /><Row k="Entered by" v={c.entered_by.name} />
    </div>
    <div className="my-5 border-2 border-black p-3 text-center"><div className="text-xs">CUTTING LENGTH</div><div className="text-3xl font-bold font-mono">{fmtQty(c.cut_length, c.uom)}</div></div>
    <div className="flex gap-8 mt-10"><Sign label="Cut by" /><Sign label="Received by (signature)" /><Sign label="Authorised signatory" /></div>
  </>);
}

function DispatchBody({ d, type }) {
  return (<>
    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
      <Row k={type === "gatepass" ? "Gate Pass No" : "Dispatch No"} v={type === "gatepass" ? d.gate_pass_no : d.dispatch_no} /><Row k="Date" v={d.date} />
      <Row k="Party" v={d.party_name} /><Row k="Bill No" v={d.bill_no} /><Row k="Vehicle" v={d.vehicle_no} /><Row k="Driver" v={`${d.driver_name || ""} ${d.driver_mobile || ""}`} />
      {type !== "gatepass" && <><Row k="Transporter" v={d.transporter} /><Row k="LR No" v={d.lr_no} /></>}
      {type === "challan" && <div className="col-span-2"><Row k="Delivery address" v={d.delivery_address} /></div>}
      {type === "handover" && <><Row k="Recipient" v={d.handover?.recipient_name || d.taken_by} /><Row k="Recipient mobile" v={d.handover?.recipient_mobile} /><Row k="Issued by" v={d.issued_by} /><Row k="Date & time" v={fmtDT(d.handover?.captured_at || d.at)} /></>}
    </div>
    <table className="w-full mt-4 text-[13px] border border-black border-collapse">
      <thead><tr className="bg-gray-100">{["#", "Description", "Brand", "Cable Size", "Drum / Piece ID", "Quantity"].map((h) => <th key={h} className="border border-black px-2 py-1 text-left">{h}</th>)}</tr></thead>
      <tbody>{d.items.map((it, i) => (
        <tr key={it.unit_id}><td className="border border-black px-2 py-1">{i + 1}</td><td className="border border-black px-2 py-1">{it.product_name}</td><td className="border border-black px-2 py-1">{it.brand}</td>
          <td className="border border-black px-2 py-1">{it.cable_size}</td><td className="border border-black px-2 py-1 font-mono">{it.unit_code}{it.mfr_drum_no ? ` (${it.mfr_drum_no})` : ""}</td><td className="border border-black px-2 py-1 font-mono text-right">{fmtQty(it.qty, it.uom)}</td></tr>
      ))}</tbody>
    </table>
    <div className="flex gap-8 mt-12">
      {type === "gatepass" ? <><Sign label="Issued by" /><Sign label="Driver" /><Sign label="Security" /></>
        : <><Sign label={`Issued by: ${d.issued_by || ""}`} /><Sign label={`Received by: ${d.handover?.recipient_name || d.taken_by || ""}`}><SigImage id={d.handover?.signature_attachment_id} /></Sign><Sign label="For ROXY CABLES" /></>}
    </div>
    {type === "handover" && d.handover && <p className="text-[11px] text-gray-500 mt-3">Signature captured by {d.handover.captured_by.name} at {fmtDT(d.handover.captured_at)}</p>}
  </>);
}

export default function PrintDoc() {
  const { type, id } = useParams();
  const [sp] = useSearchParams();
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    const url = type === "quote" ? `/quotations/${id}` : type === "bill" ? `/cuttings/bill-slip?bill_no=${encodeURIComponent(sp.get("no") || "")}` : type === "slip" ? `/cuttings/${id}` : `/dispatches/${id}`;
    api.get(url).then((r) => setDoc(r.data)).catch(() => setDoc({ error: true }));
  }, [type, id, sp]);
  if (!doc) return <div className="p-8 text-sm">Loading…</div>;
  if (doc.error) return <div className="p-8 text-sm" data-testid="print-not-found">Document not found.</div>;
  return (
    <div className="min-h-screen bg-gray-100 py-6 px-2 overflow-x-auto">
      <div className="max-w-3xl mx-auto mb-3 flex flex-wrap justify-end gap-2 no-print">{["bill", "gatepass", "challan", "quote"].includes(type) && <WhatsAppSend w={waProps(doc, type)} />}<DownloadPdf auto={sp.get("download") === "1"} name={`Roxy_${TITLES[type].split(" · ")[0].replace(/ /g, "_")}_${type === "quote" ? doc.quote_no : type === "bill" ? doc.bill_no : type === "slip" ? doc.slip_no : type === "gatepass" ? doc.gate_pass_no : doc.dispatch_no}`} /><Button onClick={() => window.print()} data-testid="print-now-btn"><Printer className="h-4 w-4 mr-1" />Print</Button></div>
      <div className="print-area max-w-3xl min-w-[680px] mx-auto bg-white text-black p-8 shadow" data-testid={`print-${type}`}>
        <div className="flex justify-between items-start border-b-2 border-black pb-3 mb-4">
          <div><div className="text-2xl font-bold tracking-tight">ROXY CABLES</div><div className="text-xs">Electrical Cables &amp; Wires · Bangalore</div></div>
          <div className="text-right"><div className="font-bold">{TITLES[type]}</div><div className="text-xs">{type === "quote" ? doc.quote_no : type === "bill" ? doc.bill_no : type === "slip" ? doc.slip_no : type === "gatepass" ? doc.gate_pass_no : doc.dispatch_no}</div></div>
        </div>
        {type === "quote" ? <QuoteBody q={doc} /> : type === "bill" ? <BillBody b={doc} /> : type === "slip" ? <SlipBody c={doc} /> : <DispatchBody d={doc} type={type} />}
        <p className="text-[10px] text-gray-500 mt-6">System generated · ROXY CABLES Inventory Management · Printed {fmtDT(new Date().toISOString())}</p>
      </div>
    </div>
  );
}
